import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CreditCard, Check, Sparkles, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Payment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan'); // 'plus', 'pro', or 'parent'
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBilling, setSelectedBilling] = useState('monthly');
  const [processingTrial, setProcessingTrial] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const plan = planParam === 'parent' ? 'parent' : (planParam === 'pro' ? 'pro' : 'plus');
  const planName = plan === 'parent' ? 'Parent Portal' : (plan === 'plus' ? 'Teacher Plus' : 'Teacher Pro');

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate('/login?redirect=/payment');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const getPricing = () => {
    const prices = {
      plus_monthly: 10,
      plus_annual: 100,
      pro_monthly: 13,
      pro_annual: 130,
      parent_monthly: 10,
      parent_annual: 100
    };
    
    const key = `${plan}_${selectedBilling}`;
    return prices[key] || 0;
  };

  const handleStartTrial = async () => {
    setProcessingTrial(true);
    
    try {
      const response = await axios.post(
        `${API}/payments/start-trial`,
        {
          plan: plan,
          billing_period: selectedBilling
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      navigate('/account?tab=subscription');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setProcessingTrial(false);
    }
  };

  const handlePayment = async () => {
    setProcessingPayment(true);
    
    try {
      // Create Razorpay order
      const orderResponse = await axios.post(
        `${API}/payments/create-razorpay-order`,
        {
          plan: plan,
          billing_period: selectedBilling
        },
        { withCredentials: true }
      );
      
      const { order_id, amount, currency, key_id } = orderResponse.data;
      
      // Razorpay checkout options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Learn with Zeenat',
        description: `${planName} - ${selectedBilling === 'annual' ? 'Annual' : 'Monthly'} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await axios.post(
              `${API}/payments/verify-razorpay`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan: plan,
                billing_period: selectedBilling
              },
              { withCredentials: true }
            );
            
            toast.success(verifyResponse.data.message);
            navigate('/account?tab=subscription');
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setProcessingPayment(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="py-12 px-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscribe to {planName}</h1>
          <p className="text-gray-600 mb-8">Get started with a 3-day free trial - no payment required upfront!</p>

          {/* Plan Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{planName}</h3>
                <p className="text-sm text-gray-600">
                  {plan === 'parent' ? 
                    'Unlimited AI tools to support your child at home' : 
                    plan === 'plus' ? 
                    'Everything you need for daily teaching with 200 AI generations/month' : 
                    'All Premium Features + Assessment Hub with unlimited AI generations'
                  }
                </p>
              </div>
            </div>

            {/* Trial Banner */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-green-900">3-Day Free Trial</div>
                  <div className="text-sm text-green-800">
                    Get immediate access - no payment details needed to start
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Options */}
            {plan !== 'parent' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-900 mb-3">Choose Billing Period:</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSelectedBilling('monthly')}
                    className={`p-4 border-2 rounded-xl transition-all ${
                      selectedBilling === 'monthly' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-bold text-gray-900">Monthly</div>
                    <div className="text-2xl font-bold text-blue-600">${plan === 'plus' ? '10' : '13'}</div>
                    <div className="text-xs text-gray-600">per month</div>
                  </button>
                  
                  <button
                    onClick={() => setSelectedBilling('annual')}
                    className={`p-4 border-2 rounded-xl transition-all relative ${
                      selectedBilling === 'annual' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="absolute -top-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      Save 17%
                    </div>
                    <div className="font-bold text-gray-900">Annual</div>
                    <div className="text-2xl font-bold text-blue-600">${plan === 'plus' ? '100' : '130'}</div>
                    <div className="text-xs text-gray-600">per year</div>
                  </button>
                </div>
              </div>
            )}

            {plan === 'parent' && (
              <div className="text-center py-2">
                <div className="text-3xl font-bold text-blue-600">$10/month</div>
                <div className="text-sm text-gray-600">Monthly subscription</div>
              </div>
            )}
          </div>

          {/* Trial Benefits */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-gray-900 mb-3">What happens next?</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <strong>Step 1:</strong> Start your 3-day free trial - instant access to all features
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <strong>Step 2:</strong> Explore all tools with no restrictions during your trial
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <strong>Step 3:</strong> After 3 days, complete payment via secure Razorpay to continue
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  No payment? No worries - you'll automatically switch back to the Free tier
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleStartTrial}
              disabled={processingTrial}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processingTrial ? (
                <>Processing...</>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Start 3-Day Free Trial
                </>
              )}
            </button>

            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">or</div>
              <button
                onClick={handlePayment}
                disabled={processingPayment}
                className="w-full bg-white border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processingPayment ? (
                  <>Processing...</>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Pay Now (${getPricing()})
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Payment Info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm text-blue-900">
              <strong>Secure Payment:</strong> All payments processed securely through Razorpay. 
              We accept Credit Cards, Debit Cards, UPI, Net Banking, and Wallets. International cards welcome.
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default Payment;
